import { useEffect, useState } from 'react';

/**
 * The server owns the clock. Every response carries X-Server-Time; we keep the
 * offset between that and the device clock and derive every displayed time
 * from it, so a wrong device clock changes nothing on screen.
 */
let offsetMs = 0;
let observed = false;

export function observeServerTime(header: string | null, requestStartedAt: number): void {
  if (!header) return;
  const server = Date.parse(header);
  if (Number.isNaN(server)) return;
  // The stamp was taken roughly half-way through the round trip.
  const midpoint = requestStartedAt + (Date.now() - requestStartedAt) / 2;
  const next = server - midpoint;
  // Ignore sub-second jitter once we have a reading.
  if (!observed || Math.abs(next - offsetMs) > 1000) offsetMs = next;
  observed = true;
}

export function serverNow(): Date {
  return new Date(Date.now() + offsetMs);
}

/** Device clock minus server clock, in seconds. For display only. */
export function clockSkewSeconds(): number {
  return Math.round(-offsetMs / 1000);
}

/** Re-renders every `everyMs` with the current server time. */
export function useServerClock(everyMs = 1000): Date {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    const id = setInterval(() => setNow(serverNow()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}
